/**
 * StreakNotificationSettings - Component for managing streak reminder preferences
 */

import { useState, useEffect } from 'react';
import { apiGet, apiPut, apiPost } from '@/utils/api';
import { useToast } from '@/components/Toast';

export default function StreakNotificationSettings({ className = '' }) {
  const [settings, setSettings] = useState({
    enabled: true,
    reminder_time: '09:00'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await apiGet('/goals/streak/settings');
      if (response.ok) {
        const data = await response.json();
        setSettings({
          enabled: data.enabled ?? true,
          reminder_time: data.reminder_time || '09:00'
        });
      }
    } catch (error) {
      console.error('Error loading streak settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      const response = await apiPut('/goals/streak/settings', newSettings);
      if (response.ok) {
        setSettings(newSettings);
        addToast('Settings saved!', 'success');
      } else {
        addToast('Failed to save settings', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      addToast('Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = () => {
    const newSettings = { ...settings, enabled: !settings.enabled };
    saveSettings(newSettings);
  };

  const handleTimeChange = (e) => {
    const newSettings = { ...settings, reminder_time: e.target.value };
    saveSettings(newSettings);
  };

  const sendTestReminder = async () => {
    setSendingTest(true);
    try {
      const response = await apiPost('/goals/streak/send-reminder');
      if (response.ok) {
        addToast('Test reminder sent to your email!', 'success');
      } else {
        addToast('Failed to send test reminder', 'error');
      }
    } catch (error) {
      console.error('Error sending test:', error);
      addToast('Error sending test reminder', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-32 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🔔</span>
          <div>
            <h3 className="font-bold">Streak Reminders</h3>
            <p className="text-xs text-emerald-100">Get notified to keep your savings streak alive</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900">Daily Reminders</p>
            <p className="text-sm text-gray-500">
              {settings.enabled 
                ? "We'll email you if you haven't saved today" 
                : "Reminders are turned off"}
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={saving}
            className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
              settings.enabled ? 'bg-emerald-500' : 'bg-gray-300'
            } ${saving ? 'opacity-50' : ''}`}
            data-testid="streak-reminder-toggle"
          >
            <span 
              className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                settings.enabled ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Reminder Time */}
        {settings.enabled && (
          <div className="pt-2 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reminder Time
            </label>
            <select
              value={settings.reminder_time}
              onChange={handleTimeChange}
              disabled={saving}
              className="w-full p-2 border border-gray-200 rounded-lg text-gray-700 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="streak-reminder-time"
            >
              <option value="07:00">7:00 AM</option>
              <option value="08:00">8:00 AM</option>
              <option value="09:00">9:00 AM</option>
              <option value="10:00">10:00 AM</option>
              <option value="12:00">12:00 PM</option>
              <option value="17:00">5:00 PM</option>
              <option value="19:00">7:00 PM</option>
              <option value="20:00">8:00 PM</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              You'll only receive a reminder if you haven't saved that day
            </p>
          </div>
        )}

        {/* Test Reminder Button */}
        {settings.enabled && (
          <div className="pt-2">
            <button
              onClick={sendTestReminder}
              disabled={sendingTest}
              className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="send-test-reminder-btn"
            >
              {sendingTest ? (
                <>
                  <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                  Sending...
                </>
              ) : (
                <>
                  <span>📧</span>
                  Send Test Reminder
                </>
              )}
            </button>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-emerald-50 rounded-lg p-3 text-sm">
          <p className="text-emerald-800">
            <strong>💡 Pro tip:</strong> Streak reminders help you build a consistent savings habit. 
            Even adding $1 counts toward your streak!
          </p>
        </div>
      </div>
    </div>
  );
}
